import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { Header } from "../components/Header";

const Section = ({ title, children }) => (
  <section className="space-y-1.5">
    <h2 className="text-sm font-semibold tracking-tight text-white">{title}</h2>
    <div className="text-xs leading-relaxed text-[#A1A1AA] space-y-2">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <main className="flex-1 overflow-y-auto max-w-3xl mx-auto w-full px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap">Legal</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">Terms of Service</h1>
            <p className="text-[11px] font-mono text-[#71717A] mt-1">
              Effective date: 2026-02-19 · Last updated: 2026-02-19
            </p>
          </div>
          <Link to="/" data-testid="terms-back-btn" className="btn-outline flex items-center gap-1.5">
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>

        <div className="border border-[#FF9F0A] bg-[rgba(255,159,10,0.06)] p-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-[#FF9F0A] mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-[#FFE5B4] leading-relaxed">
            <strong className="text-white">PLACEHOLDER — REVIEW BY QUALIFIED COUNSEL BEFORE PRODUCTION USE.</strong>{" "}
            This document is provided as a starting template only. Before relying on these Terms
            for commercial use, have them reviewed and customized by a lawyer licensed in your
            jurisdiction. ROLL.OPS makes no representation that this template is appropriate or
            sufficient for your specific business.
          </p>
        </div>

        <div className="panel p-5 space-y-5">
          <Section title="1. Acceptance of Terms">
            By accessing or using ROLL.OPS (the &ldquo;Service&rdquo;), you agree to be bound by these Terms of
            Service (&ldquo;Terms&rdquo;). If you do not agree to these Terms, do not use the Service.
          </Section>

          <Section title="2. Description of Service">
            ROLL.OPS is a <strong className="text-white">research and educational tool</strong> that
            displays publicly available options market data and provides scoring, color-coded
            heatmaps, payoff visualizations, and order-ticket information to assist self-directed
            investors evaluating covered-call rolling strategies.
          </Section>

          <Section title="3. Not a Broker, Not an Adviser">
            ROLL.OPS is <strong className="text-white">NOT</strong> a registered broker-dealer,
            investment adviser, futures commission merchant, certified financial planner, money
            transmitter, or fiduciary in any jurisdiction. The Service does not execute trades,
            route or submit orders to any broker, hold custody of funds or securities, or receive
            your broker credentials. All trades are executed by you in your own brokerage account.
          </Section>

          <Section title="4. No Financial Advice">
            Nothing displayed in the Service constitutes investment, financial, legal, tax, or
            accounting advice, nor a solicitation, recommendation, endorsement, or offer to buy or
            sell any security or derivative instrument. The Service does not consider your
            individual investment objectives, financial situation, or risk tolerance.
          </Section>

          <Section title="5. Your Responsibility for Trades">
            You are solely responsible for all trade decisions and their outcomes, including all
            gains, losses, fees, commissions, taxes, and assignment events. You agree to
            independently verify every order detail (symbol, strike, expiration, quantity, limit
            price, side, and time-in-force) in your own broker before submitting. Past performance
            and theoretical calculations shown in the Service do not predict future results.
          </Section>

          <Section title="6. Data Accuracy">
            Market data is sourced from third-party providers and may be delayed, incomplete,
            stale, or inaccurate. ROLL.OPS makes no warranty as to the accuracy, completeness, or
            timeliness of any data displayed.
          </Section>

          <Section title="7. Subscription &amp; Billing">
            Paid subscriptions are billed through Stripe, Inc. By subscribing you authorize
            recurring charges at the published rate until you cancel. You may cancel anytime via
            the in-app billing portal; cancellation takes effect at the end of the current billing
            period. No refunds are issued for partial periods except as required by applicable law.
          </Section>

          <Section title="8. Risk Disclosure">
            Options trading involves substantial risk including the risk of total loss of capital
            and is not suitable for all investors. You should not trade options unless you fully
            understand the risks involved and can afford to lose the funds you invest.
          </Section>

          <Section title="9. Limitation of Liability">
            To the maximum extent permitted by applicable law, in no event shall ROLL.OPS, its
            operator, contributors, affiliates, officers, employees, agents, or licensors be
            liable for any direct, indirect, incidental, special, consequential, exemplary, or
            punitive damages, including but not limited to loss of profits, revenue, data, or use,
            arising out of or in connection with your use of the Service, even if advised of the
            possibility of such damages. The Service is provided &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo;
            without warranty of any kind, express or implied.
          </Section>

          <Section title="10. Account Termination">
            ROLL.OPS may suspend or terminate your access to the Service at any time, with or
            without notice, for any reason including but not limited to violation of these Terms.
            You may delete your account at any time by contacting support.
          </Section>

          <Section title="11. Changes to Terms">
            ROLL.OPS may modify these Terms at any time by posting the revised version with an
            updated effective date. Continued use of the Service after such posting constitutes
            acceptance of the modified Terms.
          </Section>

          <Section title="12. Governing Law">
            These Terms are governed by the laws of the operator&apos;s home jurisdiction (to be
            specified by counsel). Any dispute arising out of or relating to these Terms shall be
            resolved by binding arbitration in accordance with the rules of a recognized
            arbitration body, except that either party may seek injunctive relief in court.
          </Section>

          <Section title="13. Contact">
            For questions about these Terms, use the in-app Contact form (Help menu → Contact us)
            or email the address listed on the Contact page.
          </Section>

          <div className="text-[10px] text-[#71717A] border-t border-[#27272A] pt-3 leading-relaxed">
            This Terms of Service document is a non-binding template provided as a starting point.
            Section headings, governing law, jurisdiction, and dispute-resolution clauses must be
            tailored to your specific business and legal jurisdiction by qualified counsel before
            relying on this document.
          </div>
        </div>
      </main>
    </div>
  );
}
